import React, { useContext, useMemo } from 'react';
import { ArrowLeft, ArrowRight, Info } from 'lucide-react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button/button';
import { Banner, BannerIcon } from '@epoch-ui/components/Banner';
import { Separator } from '@epoch-ui/components/Separator/separator';
import { queryClient } from '@api/queryClient';
import { getServiceConfig } from 'config/serviceConfig';
import UserContext from 'context/userContext';
import { formatNumberWithComma } from 'utils/format';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { LARGE_CALENDAR_SPLIT } from 'constants/featureFlags';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  useInviteGuestsModal,
  useAsyncInviteJobProcessing
} from '@event-editor/hooks/eventEditorState';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { inviteGuestsApi } from '@event-editor/api/invite-guests';

import { InviteGuestsModalProvider } from './InviteGuestsModalContext';
import { useInviteGuestsModalContext } from './hooks/useInviteGuestsModalContext';
import { InviteSelectionStep } from './components/InviteSelectionStep/InviteSelectionStep';
import { InviteSettingsStep } from './components/InviteSettingsStep';
import { SkippedGuestsBanner } from './components/SkippedGuestsBanner';

const MAX_GUESTS_ALLOWED = getServiceConfig('EVENT', 'MAX_GUESTS_ALLOWED');

const InviteGuestsModalContent: React.FC = () => {
  const event = useEditableEvent();
  const user = useContext(UserContext);
  const { showToast } = useToaster();
  const { isOpen, close } = useInviteGuestsModal();
  const asyncInviteJobProcessing = useAsyncInviteJobProcessing();

  const {
    currentStep,
    selectedUsers,
    selectedDistrolists,
    selectedCommunities,
    emailDedupeRequired,
    sendCalendarEmail,
    sendSlackInvite,
    setStep,
    clearAll,
    reset
  } = useInviteGuestsModalContext();

  const largeCalendarSplitEnabled = useFeatureFlag(LARGE_CALENDAR_SPLIT);
  const calendarEventAttendeeThreshold = user?.company.calendarEventAttendeeThreshold || 1000;

  // Filter out invitees without ID (from invalid email imports)
  const selectedUserIds = useMemo(
    () => selectedUsers.filter((u) => u.id).map((u) => parseInt(u.id!, 10)) as number[],
    [selectedUsers]
  );

  const selectedDistrolistIds = useMemo(
    () =>
      selectedDistrolists.map((distrolist) =>
        typeof distrolist.id === 'string' ? parseInt(distrolist.id, 10) : distrolist.id
      ),
    [selectedDistrolists]
  );

  const selectedCommunityIds = useMemo(
    () => selectedCommunities.map((community) => community.id),
    [selectedCommunities]
  );

  const hasSelections =
    selectedUserIds.length > 0 ||
    selectedDistrolistIds.length > 0 ||
    selectedCommunityIds.length > 0;
  const hasGroupSelections = selectedDistrolists.length > 0 || selectedCommunities.length > 0;

  const handleClose = (): void => {
    reset();
    close();
  };

  const { data: inviteeCountData, isPlaceholderData } = useQuery({
    queryKey: eventEditorQueryKeys.eventInviteeCount(
      event.id!,
      selectedUserIds,
      selectedDistrolistIds,
      selectedCommunityIds
    ),
    queryFn: () =>
      inviteGuestsApi.getInviteeTotalCounts({
        eventId: event.id!,
        distrolistIds: selectedDistrolistIds,
        communityIds: selectedCommunityIds,
        inviteeIds: selectedUserIds
      }),
    enabled: !!event.id,
    placeholderData: keepPreviousData,
    staleTime: 0 // Always fetch fresh data when selections change
  });

  const currentInviteeTotal = inviteeCountData?.currentInviteeTotal ?? 0;
  const expectedInviteeTotal = inviteeCountData?.expectedInviteeTotal ?? 0;
  const dedupeRequired = inviteeCountData?.dedupeRequired ?? false;
  const skippedDuplicateEmails = isPlaceholderData
    ? []
    : inviteeCountData?.skippedDuplicateEmails ?? [];

  const inviteGuestsMutation = useMutation({
    mutationFn: inviteGuestsApi.invite,
    onSuccess: (data, variables) => {
      const guestCount = variables.currentInviteTotal;

      // Invalidate event queries to refetch updated guest list
      queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });

      if (data.ok) {
        if (data.isAsync) {
          // Async invitation - start polling for job status
          asyncInviteJobProcessing.startPolling();
        } else {
          // Synchronous invitation - show success toast
          showToast(
            `${formatNumberWithComma(guestCount)} guest${
              guestCount === 0 || guestCount > 1 ? 's' : ''
            } invited`,
            {
              offsetLeft: true
            }
          );
        }

        // Reset modal and close it
        handleClose();
      }
    },
    onError: (error) => {
      console.error('Error inviting guests:', error);
      showToast('Error inviting guests to the event', { variant: ToasterVariant.ERROR });
    }
  });

  // Calculate guest limit and display values
  const { isGuestLimitExceeded, guestLimitErrorMessage, inviteeTotalDisplayValue } = useMemo(() => {
    let isExceeded = false;
    if (expectedInviteeTotal) {
      if (event.sessions && event.sessions.length > 0) {
        isExceeded = expectedInviteeTotal * event.sessions.length > MAX_GUESTS_ALLOWED;
      } else {
        isExceeded = expectedInviteeTotal > MAX_GUESTS_ALLOWED;
      }
    }

    let errorMessage = null;
    if (isExceeded) {
      if (event.sessions && event.sessions.length > 0) {
        errorMessage = `Events are limited to ${formatNumberWithComma(
          MAX_GUESTS_ALLOWED
        )} guests across sessions.`;
      } else {
        errorMessage = `Events are limited to ${formatNumberWithComma(MAX_GUESTS_ALLOWED)} guests.`;
      }
    }

    let displayValue = null;
    if (currentInviteeTotal > 0) {
      displayValue = currentInviteeTotal;
    } else if (hasGroupSelections) {
      displayValue = 0;
    }

    return {
      isGuestLimitExceeded: isExceeded,
      guestLimitErrorMessage: errorMessage,
      inviteeTotalDisplayValue: displayValue
    };
  }, [expectedInviteeTotal, event.sessions, currentInviteeTotal, hasGroupSelections]);

  const showClearButton = hasSelections && currentStep === 'SELECT';

  const handleInvite = (): void => {
    const userIdsToInvite = selectedUsers.filter((u) => u.id).map((u) => parseInt(u.id!, 10));

    const distrolistIdsToInvite = selectedDistrolists.map((distrolist) =>
      typeof distrolist.id === 'string' ? parseInt(distrolist.id, 10) : distrolist.id
    );

    // Apply large calendar split logic - disable calendar email if count > 4000 and feature not enabled
    const isLargeInviteDisabled = expectedInviteeTotal > 4000 && !largeCalendarSplitEnabled;
    const effectiveSendCalendarEmail = isLargeInviteDisabled ? false : sendCalendarEmail;

    inviteGuestsMutation.mutate({
      eventId: event.id!,
      userIdsToInvite,
      distrolistIdsToInvite,
      communityIdsToInvite: selectedCommunityIds,
      sendCalendarEmail: effectiveSendCalendarEmail,
      sendSlackInvite,
      deleteCalEvent: false,
      currentInviteTotal: currentInviteeTotal,
      expectedEventInviteeTotal: expectedInviteeTotal,
      hadDedupeHappened: dedupeRequired || emailDedupeRequired,
      isDontSendClicked: false
    });
  };

  return (
    <Modal open={isOpen} onOpenChange={(open) => (open ? null : handleClose())}>
      <ModalContent
        size="xlg"
        className="tw-flex tw-h-[90vh] tw-flex-col tw-overflow-hidden tw-p-0"
      >
        <ModalHeader className="tw-mb-0 tw-px-6 tw-pt-6">
          <ModalTitle>Invite guests</ModalTitle>
        </ModalHeader>

        <div className="tw-mb-6 tw-flex tw-min-h-0 tw-flex-1 tw-flex-row tw-gap-4 tw-p-6">
          {currentStep === 'SELECT' && <InviteSelectionStep />}
          {currentStep === 'SETTINGS' && (
            <InviteSettingsStep
              expectedInviteeTotal={expectedInviteeTotal}
              calendarEventAttendeeThreshold={calendarEventAttendeeThreshold}
              largeCalendarSplitEnabled={!!largeCalendarSplitEnabled}
            />
          )}
        </div>

        <Separator className="tw-my-0" />

        {/* Banners */}
        <div className="tw-px-6 tw-pt-4">
          {isGuestLimitExceeded && (
            <Banner variant="destructive" className="tw-w-fit tw-justify-start">
              <BannerIcon icon={Info} />
              <p className="tw-m-0 tw-text-sm">{guestLimitErrorMessage}</p>
            </Banner>
          )}

          {!isGuestLimitExceeded && (
            <SkippedGuestsBanner
              emails={skippedDuplicateEmails}
              dedupeRequired={(!isPlaceholderData && dedupeRequired) || emailDedupeRequired}
              helpUrl="https://help.epochapp.com/articles/invite-employees-to-an-event"
            />
          )}
        </div>

        <ModalFooter className="tw-m-0 tw-flex tw-w-full tw-items-center tw-justify-between tw-px-6 tw-py-4">
          {/* Selected invitees count */}
          <p className="tw-m-0 tw-text-sm tw-text-primary-grey">
            {inviteeTotalDisplayValue !== null
              ? `${formatNumberWithComma(inviteeTotalDisplayValue)} selected`
              : '0 selected'}
            {skippedDuplicateEmails.length > 0 &&
              ` • ${formatNumberWithComma(skippedDuplicateEmails.length)} duplicate${
                skippedDuplicateEmails.length > 1 ? 's' : ''
              } removed`}
          </p>

          {/* Action buttons */}
          <div className="tw-flex tw-flex-1 tw-justify-end tw-gap-3">
            {showClearButton && (
              <Button variant="secondary" size="sm" onClick={clearAll}>
                Clear all
              </Button>
            )}

            {currentStep === 'SELECT' && (
              <Button
                size="sm"
                onClick={() => setStep('SETTINGS')}
                disabled={!hasSelections || isGuestLimitExceeded}
                className="tw-gap-2"
              >
                Next
                <ArrowRight className="tw-size-4" />
              </Button>
            )}

            {currentStep === 'SETTINGS' && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setStep('SELECT')}
                  className="tw-gap-2"
                >
                  <ArrowLeft className="tw-size-4" />
                  Back
                </Button>
                <Button
                  size="sm"
                  onClick={handleInvite}
                  loading={inviteGuestsMutation.isPending}
                  disabled={!hasSelections}
                >
                  Invite
                </Button>
              </>
            )}
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const InviteGuestsModal: React.FC = () => {
  return (
    <InviteGuestsModalProvider>
      <InviteGuestsModalContent />
    </InviteGuestsModalProvider>
  );
};
